/**
 * Resolve which entity a notification points to from its metadata / legacy fields.
 */
export function resolveNotificationTarget(notif) {
  const meta =
    notif?.metadata && typeof notif.metadata === "object" ? notif.metadata : {};

  if (meta.entityId && meta.entityType) {
    return buildTargetFromEntity(meta.entityType, meta.entityId, meta);
  }

  if (meta.leaveId) {
    return { kind: "leave", id: String(meta.leaveId), requestId: meta.requestId };
  }
  if (meta.advanceId) {
    return {
      kind: "advance",
      id: String(meta.advanceId),
      requestId: meta.requestId,
      pendingDisbursement: Boolean(meta.pendingDisbursement),
      disbursement: Boolean(meta.disbursement),
      awaitingConfirmation: Boolean(meta.awaitingConfirmation),
      disbursementConfirmed: Boolean(meta.disbursementConfirmed),
      disbursementLedger: Boolean(meta.disbursementLedger),
    };
  }
  if (meta.cashEntitlementId) {
    return {
      kind: "cash_entitlement",
      id: String(meta.cashEntitlementId),
      pendingDisbursement: Boolean(meta.pendingDisbursement),
      disbursement: Boolean(meta.disbursement),
      awaitingConfirmation: Boolean(meta.awaitingConfirmation),
      disbursementConfirmed: Boolean(meta.disbursementConfirmed),
      disbursementLedger: Boolean(meta.disbursementLedger),
    };
  }
  if (meta.permitId) {
    return {
      kind: "request",
      id: String(meta.requestId || meta.permitId),
      requestType: "permit",
      permitId: String(meta.permitId),
    };
  }
  if (meta.weeklyRestId) {
    return {
      kind: "request",
      id: String(meta.requestId || meta.weeklyRestId),
      requestType: "weekly_rest",
      weeklyRestId: String(meta.weeklyRestId),
    };
  }
  if (meta.overtimeId) {
    return {
      kind: "overtime",
      id: String(meta.overtimeId),
      requestId: meta.requestId ? String(meta.requestId) : undefined,
    };
  }
  if (meta.wfhRequestId) {
    return {
      kind: "wfh",
      id: String(meta.wfhRequestId),
      wfhRequestId: String(meta.wfhRequestId),
    };
  }
  if (meta.attendanceId) {
    return {
      kind: "attendance",
      id: String(meta.attendanceId),
    };
  }
  if (meta.resignationId) {
    return {
      kind: "resignation",
      id: String(meta.resignationId),
      requestId: meta.requestId,
    };
  }
  if (meta.complaintId) {
    return {
      kind: "complaint",
      id: String(meta.complaintId),
      requestId: meta.requestId,
    };
  }
  if (meta.appealId) {
    return { kind: "appeal", id: String(meta.appealId) };
  }
  if (meta.penaltyId) {
    return { kind: "penalty", id: String(meta.penaltyId) };
  }
  if (meta.announcementId) {
    return {
      kind: "announcement",
      id: String(meta.announcementId),
    };
  }
  if (meta.companyPolicyId) {
    return {
      kind: "company_policy",
      id: String(meta.companyPolicyId),
    };
  }
  if (meta.requestId) {
    return {
      kind: "request",
      id: String(meta.requestId),
      requestType: meta.requestType,
    };
  }
  if (notif?.relatedRequestId) {
    return { kind: "request", id: String(notif.relatedRequestId) };
  }

  return null;
}

function buildTargetFromEntity(entityType, entityId, meta) {
  const id = String(entityId);

  switch (entityType) {
    case "leave":
      return { kind: "leave", id, requestId: meta.requestId };
    case "advance":
      return {
        kind: "advance",
        id,
        requestId: meta.requestId,
        pendingDisbursement: Boolean(meta.pendingDisbursement),
        disbursement: Boolean(meta.disbursement),
        awaitingConfirmation: Boolean(meta.awaitingConfirmation),
        disbursementConfirmed: Boolean(meta.disbursementConfirmed),
        disbursementLedger: Boolean(meta.disbursementLedger),
      };
    case "cash_entitlement":
      return {
        kind: "cash_entitlement",
        id,
        pendingDisbursement: Boolean(meta.pendingDisbursement),
        disbursement: Boolean(meta.disbursement),
        awaitingConfirmation: Boolean(meta.awaitingConfirmation),
        disbursementConfirmed: Boolean(meta.disbursementConfirmed),
        disbursementLedger: Boolean(meta.disbursementLedger),
      };
    case "permission":
    case "permit":
      return {
        kind: "request",
        id: String(meta.requestId || id),
        requestType: "permit",
      };
    case "resignation":
      return { kind: "resignation", id, requestId: meta.requestId };
    case "complaint":
      return { kind: "complaint", id, requestId: meta.requestId };
    case "investigation":
      return { kind: "investigation", id };
    case "appeal":
      return { kind: "appeal", id };
    case "penalty":
      return { kind: "penalty", id };
    case "announcement":
      return { kind: "announcement", id };
    case "company_policy":
      return { kind: "company_policy", id };
    case "weekly_rest":
      return {
        kind: "request",
        id: String(meta.requestId || id),
        requestType: "weekly_rest",
      };
    case "overtime":
      return {
        kind: "overtime",
        id,
        requestId: meta.requestId ? String(meta.requestId) : undefined,
      };
    case "attendance":
      return { kind: "attendance", id };
    case "wfh":
      return { kind: "wfh", id, wfhRequestId: id };
    case "evaluation":
      return {
        kind: "evaluation",
        id,
        event: meta.event,
        pendingCount: meta.pendingCount,
      };
    case "timesheet":
      if (meta.event === "timesheet_week_reminder") {
        return {
          kind: "timesheet",
          id,
          event: meta.event,
          weekStartDate: meta.weekStartDate,
          branchId: meta.branchId,
        };
      }
      return {
        kind: "request",
        id: String(meta.requestId || id),
        requestType: "timesheet",
        timesheetId: id,
      };
    case "request":
      return {
        kind: "request",
        id,
        requestType: meta.requestType,
      };
    default:
      return { kind: entityType, id, requestId: meta.requestId };
  }
}

export function hasNotificationTarget(notif) {
  return Boolean(resolveNotificationTarget(notif));
}

function isUnreadNotification(notif) {
  return !(notif?.isRead ?? notif?.read);
}

export function isAnnouncementNotification(notif) {
  const type = String(notif?.type || "").toLowerCase();
  if (type === "announcement") return true;
  return resolveNotificationTarget(notif)?.kind === "announcement";
}

/** Unread announcement notifications, counted by unique announcement id. */
export function countUnreadAnnouncements(notifications = []) {
  const ids = new Set();
  let untagged = 0;
  for (const notif of notifications) {
    if (!isUnreadNotification(notif) || !isAnnouncementNotification(notif)) {
      continue;
    }
    const target = resolveNotificationTarget(notif);
    if (target?.kind === "announcement" && target.id) {
      ids.add(String(target.id));
    } else {
      untagged += 1;
    }
  }
  return ids.size + untagged;
}

export function getUnreadAnnouncementIds(notifications = []) {
  const ids = new Set();
  for (const notif of notifications) {
    if (!isUnreadNotification(notif)) continue;
    const target = resolveNotificationTarget(notif);
    if (target?.kind === "announcement" && target.id) {
      ids.add(String(target.id));
    }
  }
  return ids;
}

export function getAnnouncementNotificationIds(notifications = [], announcementId) {
  if (!announcementId) return [];
  const id = String(announcementId);
  return notifications
    .filter((notif) => {
      const target = resolveNotificationTarget(notif);
      return target?.kind === "announcement" && String(target.id) === id;
    })
    .map((notif) => notif.id)
    .filter(Boolean);
}

export function isCompanyPolicyNotification(notif) {
  const type = String(notif?.type || "").toLowerCase();
  if (type === "company_policy") return true;
  return resolveNotificationTarget(notif)?.kind === "company_policy";
}

export function getUnreadCompanyPolicyIds(notifications = []) {
  const ids = new Set();
  for (const notif of notifications) {
    if (!isUnreadNotification(notif)) continue;
    const target = resolveNotificationTarget(notif);
    if (target?.kind === "company_policy" && target.id) {
      ids.add(String(target.id));
    }
  }
  return ids;
}

export function getCompanyPolicyNotificationIds(notifications = [], policyId) {
  if (!policyId) return [];
  const id = String(policyId);
  return notifications
    .filter((notif) => {
      const target = resolveNotificationTarget(notif);
      return target?.kind === "company_policy" && String(target.id) === id;
    })
    .map((notif) => notif.id)
    .filter(Boolean);
}

/**
 * Build a notification-like object from Capacitor push action / FCM data payload.
 * FCM data keys are flat strings (entityId, entityType, deepLink, …).
 */
export function notificationFromPushData(data = {}) {
  if (!data || typeof data !== "object") return null;

  const metadata = { ...data };
  // Prefer nested metadata if backend ever wraps it
  if (data.metadata && typeof data.metadata === "object") {
    Object.assign(metadata, data.metadata);
  } else if (typeof data.metadata === "string") {
    try {
      Object.assign(metadata, JSON.parse(data.metadata));
    } catch {
      // ignore malformed JSON
    }
  }

  if (data.entityId && data.entityType) {
    metadata.entityId = data.entityId;
    metadata.entityType = data.entityType;
  }

  if (data.type === "ANNOUNCEMENT" && data.entityId && !metadata.entityType) {
    metadata.entityType = "announcement";
    metadata.entityId = data.entityId;
    metadata.announcementId = data.entityId;
  }

  if (data.type === "COMPANY_POLICY" && data.entityId && !metadata.entityType) {
    metadata.entityType = "company_policy";
    metadata.entityId = data.entityId;
    metadata.companyPolicyId = data.entityId;
  }

  if (data.deepLink && !metadata.deepLink) {
    metadata.deepLink = data.deepLink;
  }

  return {
    id: data.notificationId || data.id || null,
    title: data.title || "",
    message: data.body || data.message || "",
    type: data.type || "info",
    metadata,
  };
}

/**
 * Map a resolved notification target + user role to an EssHome sidebar page id.
 */
export function resolveNotificationPageId(target, role) {
  if (!target) return "notifications";

  switch (target.kind) {
    case "leave":
      if (role === "manager" || role === "department-manager") return "approvals";
      if (role === "hr" || role === "hr-staff") return "team-approvals";
      if (role === "gm") return "approvals";
      if (role === "owner") return "requests";
      return "leave";

    case "advance":
      if (target.awaitingConfirmation) {
        return "advance-confirm";
      }
      if (target.disbursementLedger || target.disbursementConfirmed) {
        if (
          role === "hr" ||
          role === "hr-staff" ||
          role === "department-manager" ||
          role === "gm" ||
          role === "owner"
        ) {
          return "advance-ledger";
        }
      }
      if (target.pendingDisbursement) {
        return "advance-disbursement";
      }
      if (role === "hr") return "advance-approvals";
      if (role === "gm") return "approvals";
      if (role === "owner") return "requests";
      return "advance";

    case "cash_entitlement":
      if (target.awaitingConfirmation) {
        return "cash-entitlement-confirm";
      }
      if (target.disbursementLedger || target.disbursementConfirmed) {
        if (
          role === "hr" ||
          role === "hr-staff" ||
          role === "department-manager" ||
          role === "gm" ||
          role === "owner" ||
          role === "admin"
        ) {
          return "cash-entitlement-ledger";
        }
      }
      if (target.pendingDisbursement) {
        return "cash-entitlement-disbursement";
      }
      if (role === "hr" || role === "hr-staff") {
        return "cash-entitlement-create";
      }
      return "cash-entitlement-ledger";

    case "resignation":
      if (
        role === "manager" ||
        role === "department-manager" ||
        role === "hr" ||
        role === "gm" ||
        role === "owner"
      ) {
        return "resignations";
      }
      return "leave";

    case "complaint":
      if (
        role === "manager" ||
        role === "department-manager" ||
        role === "hr" ||
        role === "gm" ||
        role === "owner"
      ) {
        return "received-complaints";
      }
      return "my-complaints";

    case "appeal":
      return "appeals";

    case "investigation":
      return "investigations";

    case "penalty":
      // Recipient should land where they can appeal their own penalty.
      if (role === "department-manager") return "dashboard";
      if (role === "hr" || role === "hr-staff" || role === "gm" || role === "owner") return "penalties";
      return "home";

    case "announcement":
      return "announcements";

    case "company_policy":
      return "company-policies";

    case "attendance":
      return "attendance";

    case "wfh":
      return "wfh";

    case "evaluation":
      return "evaluations";

    case "timesheet":
      return "branch-timesheet";

    case "overtime":
      if (role === "manager" || role === "department-manager") return "approvals";
      if (role === "hr" || role === "hr-staff") return "team-approvals";
      if (role === "gm") return "approvals";
      if (role === "owner") return "requests";
      return "overtime";

    case "employee_profile":
      if (
        role === "hr" ||
        role === "hr-staff" ||
        role === "gm" ||
        role === "owner" ||
        role === "admin"
      ) {
        return "profile-completion";
      }
      return "my-profile";

    case "request":
      if (role === "manager" || role === "department-manager") return "approvals";
      if (role === "hr" || role === "hr-staff") return "team-approvals";
      if (role === "gm") return "approvals";
      if (role === "owner") return "requests";
      return "leave";

    default:
      return "notifications";
  }
}
